import { Alert, Badge, Chip, ErrorBanner, InfoTip, LoadingButton, TextArea } from '@zarya/ui';
import type { ApiError } from '@app/api/ApiError';
import {
  checksPackage,
  packageExpected,
  regoPlaceholder,
  type RegoValidateResult,
} from './PolicyModel';
import { t } from '@app/i18n';
import { withEmphasis } from '@app/i18n/emphasis';
import s from './Policies.module.css';

export interface PolicyRegoTabProps {
  texto: string;
  onTextoChange: (texto: string) => void;
  /** `slug` da política — define o pacote esperado. `null` em política nova. */
  slug: string | null;
  editable: boolean;
  /** `false` = middleware sem Rego base (FR-082). `null` = ainda não sabemos. */
  validacaoDisponivel: boolean | null;
  /** O texto atual já passou pela validação. */
  validado: boolean;
  validando: boolean;
  resultado?: RegoValidateResult | null;
  erroValidacao?: ApiError | null;
  onValidar: () => void;
}

/**
 * Aba **Rego manual** do drawer (T12, FR-081, FR-082).
 *
 * Duas verdades desconfortáveis que a tela precisa dizer em voz alta:
 *
 * 1. **Validar confere só a sintaxe.** Não diz se a regra faz o que se quer, nem
 *    se o pacote está certo — o request de `rego.validate` nem carrega o id.
 * 2. **O pacote viaja dentro do texto.** `package aim.policy.outra_coisa` grava
 *    com 200, aparece ativa e cai numa subárvore que o Rego base não consulta:
 *    uma política de negação que não nega nada. Por isso o aviso é destacado, e
 *    diz qual pacote se espera (`middleware-ordens.md › 8a`).
 */
export function PolicyRegoTab({
  texto,
  onTextoChange,
  slug,
  editable,
  validacaoDisponivel,
  validado,
  validando,
  resultado,
  erroValidacao,
  onValidar,
}: PolicyRegoTabProps) {
  const expected = packageExpected(slug);
  const aviso = checksPackage(texto, slug);
  // FR-082: "aguardando Rego base" também quando o próprio Validar responde
  // rota_ausente — o resumo da lista pode ter sido lido antes de o middleware
  // (ou o cenário de teste) mudar, e a resposta viva é a fonte mais recente.
  const indisponivel = validacaoDisponivel === false || erroValidacao?.code === 'rota_ausente';

  return (
    <div className={s.aba}>
      <div className={s.abaBarra}>
        <span className={s.legenda}>{t('humanAccess.rego.legend')}</span>
        <Chip icon="code">{t('humanAccess.rego.editable')}</Chip>
        {validado ? <Badge tone="success">{t('humanAccess.rego.validated')}</Badge> : null}
        <span className={s.espaco} />
        {editable ? (
          <>
            <LoadingButton
              variant="out"
              icon="check"
              size="sm"
              loading={validando}
              loadingLabel={t('humanAccess.rego.validating')}
              disabled={indisponivel || !texto.trim()}
              onClick={onValidar}
            >
              {t('humanAccess.rego.validate')}
            </LoadingButton>
            <InfoTip
              text={
                indisponivel
                  ? t('humanAccess.rego.validate.tipUnavailable')
                  : t('humanAccess.rego.validate.tip')
              }
              label={t('humanAccess.rego.validate.tipLabel')}
            />
          </>
        ) : null}
      </div>

      {indisponivel ? (
        <Alert tone="warning" title={t('humanAccess.rego.unavailable.title')}>
          {withEmphasis(t('humanAccess.rego.unavailable.body'))}
        </Alert>
      ) : null}

      {expected ? (
        <p className={s.pacoteEsperado}>
          {t('humanAccess.rego.expected')} <code>package {expected}</code>
        </p>
      ) : (
        <p className={s.pacoteEsperado}>
          {t('humanAccess.rego.expected')} <code>aim.policy.&lt;slug&gt;</code>{' '}
          {t('humanAccess.rego.expected.undefined')}
        </p>
      )}

      {aviso === 'divergente' ? (
        <Alert tone="danger" title={t('humanAccess.rego.diverging.title')}>
          {t('humanAccess.rego.diverging.body', { package: `package ${expected}` })}
        </Alert>
      ) : null}

      {aviso === 'ausente' ? (
        <Alert tone="warning" title={t('humanAccess.rego.missing.title')}>
          {t('humanAccess.rego.missing.body', { keyword: 'package' })}
        </Alert>
      ) : null}

      <TextArea
        mono
        rows={16}
        className={s.editor}
        value={texto}
        readOnly={!editable}
        placeholder={regoPlaceholder()}
        aria-label={t('humanAccess.rego.editor.aria')}
        onChange={(e) => onTextoChange(e.target.value)}
      />

      <p className={s.ajuda}>
        {/* Os `<code>` são identificadores do `input` do Rego — dado, não texto —
            e por isso ficam no JSX, entre os pedaços de frase do catálogo. */}
        {withEmphasis(t('humanAccess.rego.help.input'))} <code>subject.type</code> ·{' '}
        <code>subject.claims</code> {t('humanAccess.rego.help.claims')} · <code>actor.name</code> ·{' '}
        <code>action</code> · <code>resource</code> · <code>context</code>.{' '}
        {withEmphasis(t('humanAccess.rego.help.restrict.before'))} <code>deny</code>{' '}
        {t('humanAccess.rego.help.restrict.after')}
      </p>

      {erroValidacao ? (
        <ErrorBanner error={erroValidacao} title={t('humanAccess.rego.validateError')} hideLink />
      ) : null}

      {resultado && resultado.ok ? (
        <Alert tone="info" title={t('humanAccess.rego.valid.title')}>
          {t('humanAccess.rego.valid.body')}
        </Alert>
      ) : null}

      {resultado && !resultado.ok ? (
        <div className={s.errosRego} role="alert">
          <h4>{t('humanAccess.rego.refused')}</h4>
          <ul>
            {resultado.errors.map((erro, i) => (
              <li key={`${erro.line}-${i}`}>
                <span className={s.mono}>
                  {t('humanAccess.rego.line', {
                    line: erro.line ?? t('humanAccess.rego.unknownLine'),
                  })}
                  {erro.column != null ? t('humanAccess.rego.column', { column: erro.column }) : ''}
                </span>
                <span>{erro.message}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export interface PolicyPublishedTabProps {
  publicado?: Record<string, unknown> | null;
}

/**
 * Em modo interface o que vai ao PDP é **dado**, não Rego (D3) — a aba mostra,
 * somente leitura, o último documento publicado (FR-080).
 */
export function PolicyPublishedTab({ publicado }: PolicyPublishedTabProps) {
  return (
    <div className={s.aba}>
      <div className={s.abaBarra}>
        <span className={s.legenda}>{t('humanAccess.published.legend')}</span>
        <Chip icon="eye">{t('humanAccess.published.readOnly')}</Chip>
      </div>

      {publicado ? (
        <pre className={s.json}>{JSON.stringify(publicado, null, 2)}</pre>
      ) : (
        <p className={s.vazio}>{t('humanAccess.published.empty')}</p>
      )}
    </div>
  );
}
